import './UsersForm.scss';
import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { Select } from '../../../../design/inputs/select/Select';
import { AddIcon } from '../../../../design/static/icons/AddIcon';
import { RowLayout } from '../../../../modules/common/layouts/rowLayout/RowLayout';
import { ValidatedSelectInput } from '../../../components/ValidatedSelectInput';
import { EnableFor } from '../../../containers/EnableForRole';
import { InputStatus } from '../../../models/error.model';
import { ProjectRole } from '../../../models/userRole.model';
import { ITeamMember } from '../../team/models/TeamMember';
import { useProjectDetailsPermissions } from '../projectDetails.permissions';

interface IUsersFormProps {
  users: ITeamMember[];
  email: string;
  emailStatus: InputStatus;
  selectedRole: ProjectRole;
  onEmailChange: (email: string) => void;
  onRoleChange: (role: ProjectRole) => void;
  onAddNewUser: () => Promise<void>;
}

export const UsersForm = observer((props: IUsersFormProps): ReactElement => {
  const { t } = useTranslator();
  const permissions = useProjectDetailsPermissions();
  const canManageUsers = permissions.canManageUsers();

  const roleOptions = [
    { value: ProjectRole.Annotator, label: ProjectRole.Annotator },
    { value: ProjectRole.Reviewer, label: ProjectRole.Reviewer },
  ];

  const usersOptions = props.users.map((u) => ({ value: u.email, label: u.email }));

  return (
    <RowLayout
      size='md'
      className='users-form'
    >
      <EnableFor hasPermission={canManageUsers}>
        <Select
          id='user-role-dropdown'
          className='user-role-dropdown'
          placeholder={t('invitation_select_role')}
          value={roleOptions.find((x) => x.value === props.selectedRole)}
          onChange={(_, v) => {
            props.onRoleChange(v.value ?? ProjectRole.Annotator);
          }}
          options={roleOptions}
        />
        <ValidatedSelectInput
          id='user-dropdown'
          className='user-input'
          name='select'
          value={usersOptions.find((x) => x.value === props.email)}
          placeholder={t('email_address')}
          onChange={(_, v) => {
            props.onEmailChange(v.value ?? '');
          }}
          isValid={props.emailStatus.isValid}
          feedbacks={props.emailStatus.errorCodes}
          options={usersOptions}
        />
        <ButtonWithLoader
          id='add-user-button'
          color='primary'
          onClick={props.onAddNewUser}
          disabled={props.emailStatus.isValid === false}
          loaderKey='add-user-button'
          startDecorator={<AddIcon />}
        >
          {t('add')}
        </ButtonWithLoader>
      </EnableFor>
    </RowLayout>
  );
});
